import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgkitSearchComponent } from '@wtw/angularkit/ngkit-search';
import { EllipsisTitleDirective } from '../../directives/ellipsis-title.directive';
import { M2mClientService } from '../../services/m2m-client.service';
import { M2MClient } from '../../models/m2m-client.model';
import { sortByName } from '../../utils/sorting';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    EllipsisTitleDirective,
    NgkitSearchComponent,
  ],
  selector: 'fga-services-list',
  styleUrls: ['./fga-services-list.component.scss'],
  templateUrl: './fga-services-list.component.html',
})
export class FgaServicesListComponent {
  readonly selectedId = input<string | null>(null);

  readonly selected = output<string>();

  private readonly m2mClientService = inject(M2mClientService);

  readonly filterText = signal('');

  readonly services = computed<M2MClient[]>(() =>
    sortByName(this.m2mClientService.clients())
  );

  readonly filteredServices = computed<M2MClient[]>(() => {
    const query = this.filterText().trim().toLowerCase();
    const services = this.services();

    if (!query) {
      return services;
    }

    return services.filter(({ name }) => name.toLowerCase().includes(query));
  });

  readonly hasNoMatches = computed<boolean>(
    () => this.services().length > 0 && this.filteredServices().length === 0
  );

  readonly focusableId = computed<string | null>(() => {
    const filteredServices = this.filteredServices();

    if (filteredServices.length === 0) {
      return null;
    }

    const selectedServiceId = this.selectedId();
    const selectedIsVisible = filteredServices.some(({ client_id }) => client_id === selectedServiceId);

    return selectedIsVisible ? selectedServiceId : filteredServices[0].client_id;
  });

  readonly loading = this.m2mClientService.loading;

  readonly error = this.m2mClientService.error;

  onSearch(value: string): void {
    this.filterText.set(value ?? '');
  }

  clearSearch(): void {
    this.filterText.set('');
  }

  onOptionKeydown(event: KeyboardEvent): void {
    const key = event.key;

    if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') {
      return;
    }

    const currentButton = event.currentTarget as HTMLButtonElement | null;
    const buttons = currentButton
      ?.closest('[role="listbox"]')
      ?.querySelectorAll<HTMLButtonElement>('[role="option"]');

    if (!currentButton || !buttons || buttons.length === 0) {
      return;
    }

    const currentIndex = Array.from(buttons).indexOf(currentButton);

    if (currentIndex < 0) {
      return;
    }

    event.preventDefault();

    let targetIndex = currentIndex;

    switch (key) {
      case 'ArrowDown':
        targetIndex = (currentIndex + 1) % buttons.length;
        break;
      case 'ArrowUp':
        targetIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        break;
      case 'Home':
        targetIndex = 0;
        break;
      case 'End':
        targetIndex = buttons.length - 1;
        break;
    }

    buttons[targetIndex]?.focus();
  }

  onSelect(clientId: string): void {
    this.selected.emit(clientId);
  }
}
